import { Form, Input, message } from "antd";
import React, { useState, useEffect } from "react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const GetInTouch = () => {
	const [isVisible, setIsVisible] = useState(false);
	const [form] = Form.useForm();
	const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
	const [contactRef, contactVisible] = useScrollAnimation({ threshold: 0.2 });

	useEffect(() => {
		setIsVisible(true);
	}, []);

	const onFinish = (values) => {
		console.log("Form submitted:", values);
		message.success("Thank you for your message! We'll get back to you soon.");
		form.resetFields();
	};

	const contactInfo = [
		{
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
					/>
				</svg>
			),
			label: "Phone",
			value: "+1 (234) 567-891",
			href: "tel:+1234567891",
		},
		{
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
					/>
				</svg>
			),
			label: "Email",
			value: "support@appraiserassistant.com",
			href: "mailto:support@appraiserassistant.com",
		},
		{
			icon: (
				<svg
					className="w-6 h-6"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
					/>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
					/>
				</svg>
			),
			label: "Address",
			value: "2068 Boulevard Henri-Bourassa Est",
			href: null,
		},
	];

	return (
		<div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
			{/* Animated Background Blobs */}
			<div className="absolute inset-0 opacity-20">
				<div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
				<div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
				<div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
				<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
			</div>

			{/* Grid Pattern Overlay */}
			<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

			<div className="relative pt-32 pb-20 px-4">
				<div className="container mx-auto max-w-6xl">
					{/* Header */}
					<div
						ref={headerRef}
						className={`text-center mb-16 transition-all duration-700 ${
							headerVisible
								? "opacity-100 translate-y-0"
								: "opacity-0 translate-y-8"
						}`}
					>
						{/* Badge */}
						<div
							className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 transition-all duration-700 ${
								isVisible
									? "opacity-100 translate-y-0"
									: "opacity-0 -translate-y-4"
							}`}
						>
							<svg
								className="w-4 h-4 text-cyan-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
								/>
							</svg>
							<span className="text-sm text-gray-300">
								We'd love to hear from you
							</span>
						</div>

						<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
							Get in{" "}
							<span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent animate-pulse-slow">
								Touch
							</span>
						</h1>
						<p className="text-lg text-gray-300 max-w-2xl mx-auto">
							Have a question or need assistance? Reach out to us via email,
							phone, or the contact form below. We're eager to assist you.
						</p>
					</div>

					{/* Main Content */}
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
						{/* Contact Form */}
						<div
							className={`transition-all duration-700 delay-200 ${
								isVisible
									? "opacity-100 translate-x-0"
									: "opacity-0 -translate-x-8"
							}`}
						>
							<div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-10 shadow-2xl">
								<h2 className="text-2xl font-bold text-white mb-6">
									Send us a message
								</h2>

								<Form
									form={form}
									name="contact"
									onFinish={onFinish}
									layout="vertical"
								>
									<Form.Item
										name="name"
										rules={[
											{ required: true, message: "Please enter your name" },
										]}
									>
										<Input
											placeholder="Full name"
											className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
											style={{
												backgroundColor: "rgba(255,255,255,0.1)",
												color: "white",
											}}
										/>
									</Form.Item>

									<Form.Item
										name="email"
										rules={[
											{ required: true, message: "Please enter your email" },
											{ type: "email", message: "Please enter a valid email" },
										]}
									>
										<Input
											placeholder="Email address"
											className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
											style={{
												backgroundColor: "rgba(255,255,255,0.1)",
												color: "white",
											}}
										/>
									</Form.Item>

									<Form.Item name="phone">
										<Input
											placeholder="Phone number (optional)"
											className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all"
											style={{
												backgroundColor: "rgba(255,255,255,0.1)",
												color: "white",
											}}
										/>
									</Form.Item>

									<Form.Item
										name="message"
										rules={[
											{ required: true, message: "Please enter your message" },
										]}
									>
										<Input.TextArea
											placeholder="Your message"
											rows={5}
											className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:bg-white/20 focus:border-cyan-400 transition-all resize-none"
											style={{
												backgroundColor: "rgba(255,255,255,0.1)",
												color: "white",
											}}
										/>
									</Form.Item>

									<Form.Item className="mb-0">
										<button
											type="submit"
											className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
										>
											<span className="flex items-center justify-center gap-2">
												Send Message
												<svg
													className="w-5 h-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
													/>
												</svg>
											</span>
										</button>
									</Form.Item>
								</Form>
							</div>
						</div>

						{/* Contact Info */}
						<div
							ref={contactRef}
							className={`transition-all duration-700 delay-400 ${
								contactVisible
									? "opacity-100 translate-x-0"
									: "opacity-0 translate-x-8"
							}`}
						>
							{/* Contact Cards */}
							<div className="space-y-4 mb-8">
								{contactInfo.map((item, index) => (
									<div
										key={index}
										className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all hover:-translate-y-1 group"
									>
										<div className="flex items-start gap-4">
											<div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
												{item.icon}
											</div>
											<div>
												<p className="text-sm text-gray-400 mb-1">
													{item.label}
												</p>
												{item.href ? (
													<a
														href={item.href}
														className="text-white font-medium hover:text-cyan-400 transition-colors"
													>
														{item.value}
													</a>
												) : (
													<p className="text-white font-medium">{item.value}</p>
												)}
											</div>
										</div>
									</div>
								))}
							</div>

							{/* Social Links */}
							<div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
								<h3 className="text-white font-semibold mb-4">Follow Us</h3>
								<div className="flex gap-3">
									{[
										{
											icon: "M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z",
											name: "Twitter",
										},
										{
											icon: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
											name: "Facebook",
										},
										{
											icon: "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z",
											name: "LinkedIn",
										},
									].map((social, i) => (
										<button
											key={i}
											type="button"
											className="w-12 h-12 bg-white/10 border border-white/20 rounded-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/20 hover:border-cyan-500/50 transition-all hover:scale-110 cursor-pointer"
											title={social.name}
											onClick={(e) => e.preventDefault()}
										>
											<svg
												className="w-5 h-5"
												fill="currentColor"
												viewBox="0 0 24 24"
											>
												<path d={social.icon} />
											</svg>
										</button>
									))}
								</div>
							</div>

							{/* Response Time */}
							<div className="mt-8 flex items-center gap-3 text-gray-400">
								<svg
									className="w-5 h-5 text-green-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<span className="text-sm">
									We typically respond within 24 hours
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default GetInTouch;
